import { Card } from '@kingspan/design-system-components';
import { Stack, Grid, Typography, useTheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { MoreDropdown, DropdownOption } from 'components/Menu/MoreDropdown';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { getCalculationTypeText, getApplicationText } from 'pages/helpers';
import { CalculationStatusNotification } from 'pages/Calculation/CalculationStatusNotification';
import { InlineLabel } from 'pages/Projects/ProjectStack';
import { StatusLabel } from 'pages/Projects/StatusLabel';
import { ReactElement, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Application, CalculationStatus, Project, Calculation } from 'types/domainTypes';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppBaseButton } from 'components/AppBaseButton/AppBaseButton';
import ReportSelectDialog from 'components/organisms/ReportSelectDialog/ReportSelectDialog';
import { useGetCalculationQuery } from 'redux/services/projects';

enum CalcualtionAction {
  Delete,
  Duplicate,
}

interface CalculationsViewProps {
  onAddCalculations: () => void;
  project: Project;
  onDeleteCalculation: (calculationId: string) => Promise<void>;
  onDuplicateCalculation: (calculation?: Calculation) => void;
}

export function CalculationsView({
  onAddCalculations,
  project,
  onDeleteCalculation,
  onDuplicateCalculation,
}: CalculationsViewProps): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const confirmationDialogFullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  // Store the calculations in state to allow us to change their status from the SignalR notifications
  const [calculations, setCalculations] = useState(project.calculations);
  const [calculationIdToDelete, setCalculationIdToDelete] = useState('');
  const [calculationIdToDuplicate, setCalculationIdToDuplicate] = useState('');
  const [calculationToDuplicate, setCalculationToDuplicate] = useState<Calculation>();

  const calculationRes = useGetCalculationQuery(
    { projectId: project.id, calculationId: calculationIdToDuplicate },
    { refetchOnMountOrArgChange: true, skip: calculationIdToDuplicate.length < 1 }
  );

  const shouldOpenDeleteConfirmationDialog = (id: string) => calculationIdToDelete === id;
  const shouldOpenDuplicateConfirmationDialog = (id: string) => calculationIdToDuplicate === id;

  const closeConfirmationDialog = (dialog: CalcualtionAction): void => {
    switch (dialog) {
      case CalcualtionAction.Delete:
        setCalculationIdToDelete('');
        break;
      case CalcualtionAction.Duplicate:
        setCalculationIdToDuplicate('');
        break;
      default:
        return;
    }
  };

  const confirmConfirmationDialog = async (dialog: CalcualtionAction, calculationId: string) => {
    switch (dialog) {
      case CalcualtionAction.Delete:
        setCalculationIdToDelete('');
        await onDeleteCalculation(calculationId);
        break;
      case CalcualtionAction.Duplicate:
        setCalculationIdToDuplicate('');
        onDuplicateCalculation(calculationToDuplicate);
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (calculationRes.isSuccess) {
      setCalculationToDuplicate(calculationRes.data);
    }
  }, [calculationRes.data, calculationRes.isSuccess]);

  useEffect(() => setCalculations(project.calculations), [project.calculations]);

  const [isReportExportDialogOpen, setReportExportDialogOpen] = useState(false);

  const anyCalcsWithPassOrFailStatus = (calculation: Calculation) => {
    return (
      calculation.status === CalculationStatus.Fail ||
      calculation.status === CalculationStatus.Pass ||
      calculation.status === CalculationStatus.Completed
    );
  };

  return (
    <Stack paddingTop="30px">
      <CalculationStatusNotification projectId={project.id} calculations={calculations} setCalculations={setCalculations} />
      <Stack direction="row" spacing={2} sx={{ pb: 4 }}>
        <Typography variant="h4" fontFamily="KingspanBrownTT-Bold" sx={{ flex: 1 }}>
          {t('project-details-page.label-calculations')}
        </Typography>
        {(calculations || []).some(anyCalcsWithPassOrFailStatus) && (
          <AppBaseButton onClick={() => setReportExportDialogOpen(true)} variant="outlined" sx={{ backgroundColor: '#fff' }}>
            {t('project-details-page.action-export-reports')}
          </AppBaseButton>
        )}
        <ShrinkToIconButton buttonText={t('project-details-page.add-calculation-button')} onClick={onAddCalculations} variant="contained" />
      </Stack>
      <Grid container spacing={3} wrap="wrap" marginBottom="24px">
        {calculations.map((calculation) => (
          <Grid item xs={12} sm={6} xl={4} key={calculation.details.name} data-testid={`calculation-card-${calculation.id}`}>
            <Card
              cardTitle={calculation.details.name}
              handleClick={() => {
                navigate(`/project/${project.id}/calculation/${calculation.id}`, { state: { project, calculation } });
              }}
              action={
                <MoreDropdown data-testid="calculations-view-dropdown">
                  <DropdownOption
                    callback={() => {
                      setCalculationIdToDelete(calculation.id);
                    }}
                    label={t('project-details-page.label-delete-calculation')}
                  />
                  <DropdownOption
                    callback={() => {
                      setCalculationIdToDuplicate(calculation.id);
                    }}
                    label={t('project-details-page.label-duplicate-calculation')}
                  />
                </MoreDropdown>
              }
            >
              <Grid container direction="column" height={122} justifyContent="space-between" marginTop={1} wrap="nowrap">
                <Grid item zeroMinWidth sx={{ mt: 0.5 }}>
                  <Stack>
                    <InlineLabel
                      label="calculation.label-created-by"
                      value={calculation.createdByName ? calculation.createdByName : calculation.createdBy}
                    />
                    <InlineLabel label="calculation.label-type" value={getCalculationTypeText(calculation.details.type)} />
                    <InlineLabel
                      label="calculation.label-application"
                      value={getApplicationText(calculation.details.application as Application)}
                    />
                  </Stack>
                </Grid>
                <Grid item container sx={{ '& .MuiChip-root': { margin: '0 15px 3px 0' } }}>
                  <Grid item>
                    <StatusLabel size="small" value={calculation.status} isCalculationStatus />
                  </Grid>
                  <Grid item>
                    <StatusLabel
                      size="small"
                      value={calculation.standard ? t('calculation.calculation-standard') : t('calculation.project-standard')}
                      isCalculationStatus
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Card>
            {shouldOpenDeleteConfirmationDialog(calculation.id) && (
              <ConfirmationDialog
                isOpen={shouldOpenDeleteConfirmationDialog(calculation.id)}
                isFullscreen={confirmationDialogFullScreen}
                handleClose={() => closeConfirmationDialog(CalcualtionAction.Delete)}
                handleConfirm={() => confirmConfirmationDialog(CalcualtionAction.Delete, calculation.id)}
                title={t('project-details-page.delete-calculation-dialog.header')}
                titleIcon={<WarningAmberIcon />}
                questionText={t('project-details-page.delete-calculation-dialog.question', { calculationName: calculation.details.name })}
                cancelText={t('project-details-page.delete-calculation-dialog.action-cancel')}
                confirmText={t('project-details-page.delete-calculation-dialog.action-confirm')}
              />
            )}
            {shouldOpenDuplicateConfirmationDialog(calculation.id) && (
              <ConfirmationDialog
                isOpen={shouldOpenDuplicateConfirmationDialog(calculation.id)}
                isFullscreen={confirmationDialogFullScreen}
                handleClose={() => closeConfirmationDialog(CalcualtionAction.Duplicate)}
                handleConfirm={() => confirmConfirmationDialog(CalcualtionAction.Duplicate, calculation.id)}
                title={t('project-details-page.duplicate-calculation-dialog.header')}
                titleIcon={<WarningAmberIcon />}
                questionText={t('project-details-page.duplicate-calculation-dialog.question', {
                  calculationName: calculation.details.name,
                })}
                cancelText={t('project-details-page.duplicate-calculation-dialog.action-cancel')}
                confirmText={t('project-details-page.duplicate-calculation-dialog.action-confirm')}
              />
            )}
          </Grid>
        ))}
      </Grid>
      {isReportExportDialogOpen && (
        <ReportSelectDialog project={project} open={isReportExportDialogOpen} onClose={() => setReportExportDialogOpen(false)} />
      )}
    </Stack>
  );
}
